/**
 *
 * FNAnalysisFNStatementsBalanceSheet
 *
 */

import React from "react";
import HelpIconButtonWithTooltipAndTypeformModal from "../../typeform/HelpIconButtonWithTooltipAndTypeformModal";
import TypeformContent from "../../typeform/TypeformContent";

// Components

/**
 * Price in millions.
 */
const PriceInMillions = ({ price, baseClassName }) => {
  if (typeof price !== "number") {
    return <div className={baseClassName}>n/a</div>;
  }

  let newPrice;
  if (price < 0) {
    newPrice = (price / 1000000).toFixed(2) * -1;
    return (
      <div className={`font-red ${baseClassName}`}>{`-$${newPrice}M`}</div>
    );
  } else {
    newPrice = (price / 1000000).toFixed(2);
    return <div className={baseClassName}>{`$${newPrice}M`}</div>;
  }
};

/**
 * Table Head
 */

const TableHead = ({ current_year }) => (
  <thead>
    <tr>
      <th className="table-title text-center">
        <span />
      </th>
      <th className="table-title title-gray text-center">
        YEAR {current_year}
      </th>
    </tr>
  </thead>
);

/**
 * Table row contents
 */
const TableRowContentWhite = ({ balanceSheet }) => (
  <tr className="text-center">
    <th className="description">{balanceSheet.name}</th>
    <td>
      <PriceInMillions
        baseClassName="text-center"
        price={balanceSheet.data.value}
      />
    </td>
  </tr>
);

/**
 * Table row contents with gray background color.
 */

const TableRowContentGray = ({ balanceSheet }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description">{balanceSheet.name}</th>
    <td>
      <PriceInMillions
        baseClassName="text-center"
        price={balanceSheet.data.value}
      />
    </td>
  </tr>
);

/**
 * Table row contents with dim gray background color.
 */

const TableRowContentDimGray = ({ balanceSheet }) => (
  <tr className="text-center dim-gray none-shadow">
    <th className="description">
      <strong>{balanceSheet.name}</strong>
    </th>
    <td>
      <PriceInMillions
        baseClassName="text-center"
        price={balanceSheet.data.value}
      />
    </td>
  </tr>
);

/**
 * Table row title.
 */

const TableRowContentHeader = ({ title }) => (
  <tr className="text-center">
    <th className="sub-title title-sky-blue">{title}</th>
    <td>
      <div className="row flex">
        <div className="col-xs-6" />
        <div className="col-xs-6" />
        <div className="col-xs-6" />
      </div>
    </td>
  </tr>
);

/**
 * Table row sub header.
 */

const TableRowContentSubheader = ({ title }) => (
  <tr className="text-center">
    <th className="sub-title">{title}</th>
    <td>
      <div className="row flex">
        <div className="col-xs-6" />
        <div className="col-xs-6" />
        <div className="col-xs-6" />
      </div>
    </td>
  </tr>
);

/**
 *
 * FNAnalysisFNStatementsBalanceSheet
 *
 */
function BalanceSheetWidget({
  balanceSheet,
  current_year,
  className = "team-reports-with-scroll"
}) {
  const {
    accountsPayable,
    accountsReceivable,
    accruedExpenses,
    accumulatedDepreciation,
    additionalPaidinCapital,
    begRetainedEarnings,
    cash,
    commonStock,
    deferredRevenues,
    dividends,
    inventory,
    longTermDebt,
    netIncome,
    otherCurrentLiabilities,
    otherLongTermLiabilities,
    ppe,
    preferredStock,
    prepaidExpenses,
    shortTermDebt,
    supplies,
    totalAssets,
    totalCurrentAssets,
    totalCurrentLiabilities,
    totalEquity,
    totalLiabilities,
    totalLongTermAssets,
    totalLongTermLiabilities
  } = balanceSheet;

  return (
    <div className={`panel-dashboard-white-full ${className}`}>
      <h5 className="title-sky-blue">
        Balance Sheet
        <HelpIconButtonWithTooltipAndTypeformModal
          iconSize="sm"
          {...TypeformContent.BALANCE_SHEET}
        />
      </h5>
      <div className="table-responsive fn-results-table">
        <table className="table border-analysis mb-20">
          <TableHead current_year={current_year} />
          {Object.keys(accountsPayable.data).length > 0 ||
          Object.keys(accountsReceivable.data).length > 0 ||
          Object.keys(accruedExpenses.data).length > 0 ? (
            <tbody>
              <TableRowContentSubheader title="Current Assets:" />
              <TableRowContentGray balanceSheet={cash} />
              <TableRowContentWhite balanceSheet={accountsReceivable} />
              <TableRowContentGray balanceSheet={inventory} />
              <TableRowContentWhite balanceSheet={prepaidExpenses} />
              <TableRowContentGray balanceSheet={supplies} />
              <TableRowContentDimGray balanceSheet={totalCurrentAssets} />
              <TableRowContentSubheader title="" />
              <TableRowContentSubheader title="Long Term Assets:" />
              <TableRowContentGray balanceSheet={ppe} />
              <TableRowContentWhite balanceSheet={accumulatedDepreciation} />
              <TableRowContentSubheader title="" />
              <TableRowContentDimGray balanceSheet={totalLongTermAssets} />
              <TableRowContentHeader title="" />
              <TableRowContentDimGray balanceSheet={totalAssets} />
              <TableRowContentHeader title="" />
              <TableRowContentHeader title="Liabilities" />
              <TableRowContentSubheader title="Current Liabilities" />
              <TableRowContentGray balanceSheet={deferredRevenues} />
              <TableRowContentWhite balanceSheet={accountsPayable} />
              <TableRowContentGray balanceSheet={accruedExpenses} />
              <TableRowContentWhite balanceSheet={shortTermDebt} />
              <TableRowContentGray balanceSheet={otherCurrentLiabilities} />
              <TableRowContentDimGray balanceSheet={totalCurrentLiabilities} />
              <TableRowContentHeader title="" />
              <TableRowContentSubheader title="Long Term Liabilities" />
              <TableRowContentGray balanceSheet={longTermDebt} />
              <TableRowContentWhite balanceSheet={otherLongTermLiabilities} />
              <TableRowContentHeader title="" />
              <TableRowContentDimGray balanceSheet={totalLongTermLiabilities} />
              <TableRowContentHeader title="" />
              <TableRowContentDimGray balanceSheet={totalLiabilities} />
              <TableRowContentHeader title="" />
              <TableRowContentHeader title="Equity" />
              <TableRowContentGray balanceSheet={commonStock} />
              <TableRowContentWhite balanceSheet={preferredStock} />
              <TableRowContentGray balanceSheet={additionalPaidinCapital} />
              <TableRowContentHeader title="Retained Earnings:" />
              <TableRowContentGray balanceSheet={begRetainedEarnings} />
              <TableRowContentWhite balanceSheet={netIncome} />
              <TableRowContentGray balanceSheet={dividends} />
              <TableRowContentDimGray balanceSheet={totalEquity} />
            </tbody>
          ) : (
            <tbody />
          )}
        </table>
      </div>
    </div>
  );
}

export default BalanceSheetWidget;



// WEBPACK FOOTER //
// ./src/components/finance/widgets/balance-sheet.js